import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import WhatsAppLink from '@/components/WhatsAppLink';
import { SpinnerGap, ArrowLeft, PencilSimple, Phone, ChatText, ListChecks, Lightning, FloppyDisk, X, Plus } from '@phosphor-icons/react';

const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return d; } };

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('activities');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(null);
  const [addForm, setAddForm] = useState({});

  const fetchData = useCallback(() => {
    api.post(`/contactos/marketing/${id}/detail`)
      .then(r => {
        if (r.data?.success) {
          setData(r.data);
          setForm({ Nome: r.data.detail?.Nome || '', Email: r.data.detail?.Email || '', Telemovel: r.data.detail?.Telemovel || '', ID_TipoStatusContacto: r.data.detail?.ID_TipoStatusContacto || 'NEW' });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/contactos/marketing/${id}/edit`, form);
      setEditing(false);
      fetchData();
    } catch (e) { console.error("Edit marketing contact failed", e); }
    setSaving(false);
  };

  const handleAdd = async (type) => {
    try {
      await api.post(`/contactos/marketing/${id}/${type}`, addForm);
      setAdding(null);
      setAddForm({});
      fetchData();
    } catch (e) { console.error("Add marketing contact failed", e); }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  if (!data?.detail) {
    return (
      <div data-testid="contact-detail-empty" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <button data-testid="back-btn" onClick={() => navigate('/contactos')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <p className="text-sm text-[#6B7280]">Contacto não encontrado</p>
        </div>
      </div>
    );
  }

  const d = data.detail;
  const statusTypes = data.statusTypes || [];

  const tabs = [
    { key: 'activities', label: 'Atividades', icon: Lightning, count: (data.activities || []).length },
    { key: 'observations', label: 'Observações', icon: ChatText, count: (data.observations || []).length },
    { key: 'calls', label: 'Chamadas', icon: Phone, count: (data.calls || []).length },
    { key: 'tasks', label: 'Tarefas', icon: ListChecks, count: (data.tasks || []).length },
  ];

  return (
    <div data-testid="contact-detail-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-btn" onClick={() => navigate('/contactos')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            Detalhe do Contacto
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{d.Nome}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Contact Card */}
        <div className="lg:col-span-1">
          <div data-testid="contact-card" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            {/* Avatar header */}
            <div className="bg-gradient-to-b from-[#0F1829]/10 to-white px-5 pt-6 pb-4 text-center">
              <div className="w-16 h-16 rounded-full bg-[#0F1829] text-white flex items-center justify-center text-xl font-bold mx-auto mb-3">
                {(d.Nome || '?')[0].toUpperCase()}
              </div>
              {!editing ? (
                <>
                  <h2 className="text-lg font-semibold text-[#111827]">{d.Nome}</h2>
                  <p className="text-sm text-[#6B7280] mt-0.5">{d.StatusContacto}</p>
                </>
              ) : (
                <div className="space-y-2 text-left">
                  <input data-testid="edit-name" value={form.Nome} onChange={e => setForm({ ...form, Nome: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Nome" />
                  <select data-testid="edit-status" value={form.ID_TipoStatusContacto} onChange={e => setForm({ ...form, ID_TipoStatusContacto: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none">
                    {statusTypes.map(s => <option key={s.ID_TipoStatusContacto} value={s.ID_TipoStatusContacto}>{s.Descricao}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Info fields */}
            <div className="px-5 py-4 space-y-3 border-t border-[#E5E7EB]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-0.5">Email</p>
                {!editing ? (
                  <p data-testid="contact-email" className="text-sm text-[#111827]">{d.Email || '-'}</p>
                ) : (
                  <input data-testid="edit-email" value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Email" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-0.5">Telemóvel</p>
                {!editing ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={d.Telemovel ? `tel:${d.Telemovel}` : undefined}
                      data-testid="contact-phone"
                      className={`text-sm ${d.Telemovel ? 'text-[#0F1829] hover:underline' : 'text-[#9CA3AF]'}`}
                    >
                      {d.Telemovel || '-'}
                    </a>
                    {d.Telemovel && (
                      <WhatsAppLink
                        phone={d.Telemovel}
                        size={11}
                        className="w-5 h-5"
                        idNegocio={d.UltimaLead_ID_Negocio || null}
                        firstName={(d.Nome || '').split(' ')[0] || ''}
                        message={`Olá {Nome}, sou consultor IHTP e contacto-o no seguimento do seu pedido. Como posso ajudar?`}
                      />
                    )}
                  </div>
                ) : (
                  <input data-testid="edit-phone" value={form.Telemovel} onChange={e => setForm({ ...form, Telemovel: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Telemóvel" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-0.5">Data Criação</p>
                <p className="text-sm text-[#111827]">{fmtDate(d.DataCriacao)}</p>
              </div>
            </div>

            {/* Edit buttons */}
            <div className="px-5 py-3 border-t border-[#E5E7EB] flex gap-2">
              {!editing ? (
                <button data-testid="edit-contact-btn" onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] transition-colors w-full justify-center">
                  <PencilSimple size={14} /> Editar
                </button>
              ) : (
                <>
                  <button data-testid="save-contact-btn" onClick={handleSave} disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] transition-colors flex-1 justify-center disabled:opacity-50">
                    <FloppyDisk size={14} /> {saving ? 'A guardar...' : 'Guardar'}
                  </button>
                  <button data-testid="cancel-edit-btn" onClick={() => { setEditing(false); setForm({ Nome: d.Nome, Email: d.Email, Telemovel: d.Telemovel, ID_TipoStatusContacto: d.ID_TipoStatusContacto }); }}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-[#F9FAFB] transition-colors">
                    <X size={14} /> Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Timeline / Tabs */}
        <div className="lg:col-span-2">
          <div data-testid="contact-timeline" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-[#E5E7EB] overflow-x-auto">
              {tabs.map(t => (
                <button key={t.key} data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    tab === t.key ? 'border-[#0F1829] text-[#0F1829]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                  }`}>
                  <t.icon size={14} /> {t.label} <span className="text-[10px] opacity-70">({t.count})</span>
                </button>
              ))}
            </div>

            {/* Add button */}
            {tab !== 'activities' && (
              <div className="px-4 pt-3">
                {adding !== tab ? (
                  <button data-testid={`add-${tab}-btn`} onClick={() => { setAdding(tab); setAddForm({}); }}
                    className="flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:underline">
                    <Plus size={12} /> Adicionar {tab === 'observations' ? 'observação' : tab === 'calls' ? 'chamada' : 'tarefa'}
                  </button>
                ) : (
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 space-y-2 mb-2">
                    {tab === 'observations' && (
                      <textarea data-testid="add-obs-text" value={addForm.Descricao || ''} onChange={e => setAddForm({ ...addForm, Descricao: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded resize-none focus:ring-2 focus:ring-[#0F1829] outline-none" rows={3} placeholder="Escreva a observação..." />
                    )}
                    {tab === 'calls' && (
                      <>
                        <input data-testid="add-call-result" value={addForm.ResultadoChamada || ''} onChange={e => setAddForm({ ...addForm, ResultadoChamada: e.target.value })}
                          className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Resultado da chamada" />
                        <input data-testid="add-call-date" type="date" value={addForm.DataChamada || ''} onChange={e => setAddForm({ ...addForm, DataChamada: e.target.value })}
                          className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" />
                      </>
                    )}
                    {tab === 'tasks' && (
                      <>
                        <input data-testid="add-task-name" value={addForm.Tarefa || ''} onChange={e => setAddForm({ ...addForm, Tarefa: e.target.value })}
                          className="w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Tarefa" />
                        <textarea data-testid="add-task-obs" value={addForm.Observacao || ''} onChange={e => setAddForm({ ...addForm, Observacao: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded resize-none focus:ring-2 focus:ring-[#0F1829] outline-none" rows={2} placeholder="Observação (opcional)" />
                      </>
                    )}
                    <div className="flex gap-2">
                      <button data-testid={`save-${tab}-btn`} onClick={() => handleAdd(tab === 'observations' ? 'observation' : tab === 'calls' ? 'call' : 'task')}
                        className="px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] transition-colors">
                        Guardar
                      </button>
                      <button onClick={() => setAdding(null)} className="px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-white transition-colors">
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab content */}
            <div className="p-4">
              {tab === 'activities' && (
                <div data-testid="activities-list" className="space-y-2">
                  {(data.activities || []).length === 0 && <p className="text-sm text-[#6B7280] text-center py-4">Sem atividades registadas</p>}
                  {(data.activities || []).map((a, i) => (
                    <div key={`act-${a.DataCriacao || ''}-${a.TipoAtividade || ''}-${i}`} data-testid={`activity-${i}`} className="flex items-start gap-3 py-2 border-b border-[#F3F4F6] last:border-0">
                      <div className="w-2 h-2 rounded-full bg-[#0F1829] mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-[#111827]">{a.TipoAtividade}</p>
                        <p className="text-[10px] text-[#6B7280] mt-0.5">{fmtDate(a.DataCriacao)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'observations' && (
                <div data-testid="observations-list" className="space-y-2">
                  {(data.observations || []).length === 0 && <p className="text-sm text-[#6B7280] text-center py-4">Sem observações registadas</p>}
                  {(data.observations || []).map((o, i) => (
                    <div key={`obs-${o.DataCriacao || ''}-${i}`} data-testid={`observation-${i}`} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                      <p className="text-sm text-[#111827]">{o.Descricao}</p>
                      <p className="text-[10px] text-[#6B7280] mt-1">{fmtDate(o.DataCriacao)}</p>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'calls' && (
                <div data-testid="calls-list" className="space-y-2">
                  {(data.calls || []).length === 0 && <p className="text-sm text-[#6B7280] text-center py-4">Sem chamadas registadas</p>}
                  {(data.calls || []).map((c, i) => (
                    <div key={`call-${c.DataChamada || ''}-${c.DataCriacao || ''}-${i}`} data-testid={`call-${i}`} className="flex items-start gap-3 py-2 border-b border-[#F3F4F6] last:border-0">
                      <Phone size={16} className="text-[#6B7280] mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-[#111827]">{c.ResultadoChamada || 'Sem resultado'}</p>
                        <p className="text-[10px] text-[#6B7280] mt-0.5">Chamada: {fmtDate(c.DataChamada)} | Criado: {fmtDate(c.DataCriacao)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'tasks' && (
                <div data-testid="tasks-list" className="space-y-2">
                  {(data.tasks || []).length === 0 && <p className="text-sm text-[#6B7280] text-center py-4">Sem tarefas registadas</p>}
                  {(data.tasks || []).map((t, i) => (
                    <div key={`task-${t.DataCriacao || ''}-${i}`} data-testid={`task-${i}`} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                      <p className="text-sm font-medium text-[#111827]">{t.Tarefa}</p>
                      {t.Observacao && <p className="text-xs text-[#6B7280] mt-1">{t.Observacao}</p>}
                      <p className="text-[10px] text-[#6B7280] mt-1">{fmtDate(t.DataCriacao)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
